/**
 * TerrainAnalysisPanel — PluginDock panel for terrain analysis tools.
 *
 * Combines:
 * - Elevation profile sampling + chart
 * - Slope/aspect mode switcher
 * - Extension points for future analysis tools
 */

import { useState, useCallback } from 'react';
import type { AnnotationEntry } from '@geosoft/viewer-sdk';
import { Chip, tokens } from '@/design';
import { ElevationProfileChart } from '@/components/terrain/ElevationProfileChart';
import type { ElevationProfileSample } from '@/sdk-stub/gtwin-sdk/terrain-types';
import {
  extractProfileCoords,
  INITIAL_STATE,
  type ElevationProfilePluginState,
} from './ElevationProfilePlugin';
import {
  INITIAL_SLOPE_STATE,
  nextMode,
  type SlopeAnalysisState,
} from './SlopeAnalysisPlugin';

export interface TerrainAnalysisPanelProps {
  annotations: ReadonlyArray<AnnotationEntry>;
  'data-testid'?: string;
}

export function TerrainAnalysisPanel({
  annotations,
  'data-testid': testId,
}: TerrainAnalysisPanelProps) {
  const [_profileState, _setProfileState] =
    useState<ElevationProfilePluginState>(INITIAL_STATE);
  const [slopeState, setSlopeState] =
    useState<SlopeAnalysisState>(INITIAL_SLOPE_STATE);
  const [samples, _setSamples] = useState<ElevationProfileSample[]>([]);

  const coords = extractProfileCoords(annotations);

  // @ts-expect-error TS6133 — hook must be called for rules-of-hooks compliance
  const cycleSlopeMode = useCallback(() => {
    setSlopeState((prev) => ({
      ...prev,
      mode: nextMode(prev.mode),
    }));
  }, []);

  return (
    <div
      data-testid={testId ?? 'terrain-analysis-panel'}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}
    >
      <section>
        <h4
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: tokens.color.ink.secondary,
            margin: `0 0 ${tokens.space[1]}px`,
          }}
        >
          Elevation Profile
        </h4>
        {coords ? (
          <ElevationProfileChart samples={samples} />
        ) : (
          <p
            style={{
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              color: tokens.color.ink.secondary,
            }}
          >
            Draw a cross-section line to sample terrain elevation.
          </p>
        )}
      </section>

      <section>
        <h4
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: tokens.color.ink.secondary,
            margin: `0 0 ${tokens.space[1]}px`,
          }}
        >
          Analysis mode
        </h4>
        <div style={{ display: 'flex', gap: tokens.space[1], flexWrap: 'wrap' }}>
          {(['elevation', 'slope', 'aspect'] as const).map((m) => (
            <Chip
              key={m}
              icon={
                m === 'elevation'
                  ? 'terrain'
                  : m === 'slope'
                    ? 'landscape'
                    : 'north'
              }
              variant={slopeState.mode === m ? 'info' : 'neutral'}
              label={m.charAt(0).toUpperCase() + m.slice(1)}
              pressable
              pressed={slopeState.mode === m}
              onClick={() => setSlopeState((prev) => ({ ...prev, mode: m }))}
              data-testid={`terrain-analysis-${m}`}
            />
          ))}
        </div>
      </section>

      <section>
        <h4
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: tokens.color.ink.secondary,
            margin: `0 0 ${tokens.space[1]}px`,
          }}
        >
          Future tools
        </h4>
        <div style={{ display: 'flex', gap: tokens.space[1], flexWrap: 'wrap' }}>
          <Chip icon="water" variant="neutral" label="Flood sim" />
          <Chip icon="wb_sunny" variant="neutral" label="Solar analysis" />
          <Chip icon="difference" variant="neutral" label="Cut & fill" />
        </div>
      </section>
    </div>
  );
}
